import React, { Component, ReactNode } from 'react';
import { Loader2, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { captureException, addBreadcrumb } from '@/lib/sentry';
import { logger } from '@/utils/logger';
import { safeReload } from '@/utils/safeReload';

interface Props {
  children: ReactNode;
}

interface State {
  hasRouterError: boolean;
  error?: Error;
}

/**
 * Error boundary specifically for Router context errors
 */
export class RouterErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasRouterError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    // Check if this is a router context error
    if (error.message.includes('useContext') || 
        error.message.includes('useNavigate') ||
        error.message.includes('Router')) {
      return { hasRouterError: true, error };
    }
    // Re-throw other errors to be caught by other boundaries
    throw error;
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    logger.error('Router Error Boundary caught an error:', error.message);
    
    // Send router errors to Sentry
    addBreadcrumb('Router error boundary triggered', 'navigation', {
      url: window.location.href,
      errorMessage: error.message
    });
    
    captureException(error, {
      component: 'RouterErrorBoundary',
      action: 'componentDidCatch',
      componentStack: errorInfo.componentStack?.substring(0, 1000),
      isRouterError: true
    });
  }

  private handleReload = () => {
    safeReload();
  };

  private handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasRouterError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-destructive/5 via-background to-destructive/10">
          <div className="max-w-md w-full mx-4">
            <div className="bg-card p-8 rounded-lg border shadow-lg text-center">
              <AlertTriangle className="h-12 w-12 text-destructive mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-foreground mb-2">
                Erreur de navigation
              </h2>
              <p className="text-muted-foreground mb-6">
                Une erreur s'est produite lors de l'initialisation de la navigation. 
                Veuillez recharger la page.
              </p>
              <div className="flex gap-3 justify-center">
                <Button onClick={this.handleReload} variant="default">
                  <Loader2 className="mr-2 h-4 w-4" />
                  Recharger
                </Button>
                <Button onClick={this.handleGoHome} variant="outline">
                  Accueil
                </Button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}